import type { BindingHelpers } from 'vuex-class/lib/bindings';
import { namespace } from 'vuex-class';
import { ECriiptoStatus } from '../types/enums/criipto-status';
import type { ISigningState } from './signing.types';
import { EAfterSignStep } from '@signing-page/signing/types/enums/after-sign-step';
import { EBankIdSigningType, EBankIdStep } from '@signing-page/signing/types/enums/bankid-se';

export const signingNamespace = 'signing';
export const Signing: BindingHelpers = namespace(signingNamespace);

export enum ESigningAction {
  StartBankIdTransaction = 'startBankIdTransaction',
  StartBankIdTransactionSuccess = 'startBankIdTransactionSuccess',
  StartBankIdTransactionFailed = 'startBankIdTransactionFailed',
  PollBankIdTransaction = 'pollBankIdTransaction',
  PollBankIdTransactionSuccess = 'pollBankIdTransactionSuccess',
  PollBankIdTransactionFailed = 'pollBankIdTransactionFailed',
  SetError = 'setError',
  CancelBankIdTransaction = 'cancelBankIdTransaction',
  RestartPoll = 'restartPoll',
  ResetBankId = 'resetBankId',
  SetBankIdQueryParams = 'setBankIdQueryParams',
  SetCriiptoQueryParams = 'setCriiptoQueryParams',
  SetBankIdState = 'setBankIdState',
  CheckVerificationStep = 'checkVerificationStep',
  CheckVerificationStepSuccess = 'checkVerificationStepSuccess',
  CheckVerificationStepFailed = 'checkVerificationStepFailed',
  VerifySignature = 'verifySignature',
  VerifySignatureSuccess = 'verifySignatureSuccess',
  VerifySignatureFailed = 'verifySignatureFailed',
  VerifyQna = 'verifyQna',
  VerifyQnaSuccess = 'verifyQnaSuccess',
  VerifyQnaFailed = 'verifyQnaFailed',
  SendSmsCode = 'sendSmsCode',
  SendSmsCodeFailed = 'sendSmsCodeFailed',
  VerifySms = 'verifySms',
  VerifySmsSuccess = 'verifySmsSuccess',
  VerifySmsFailed = 'verifySmsFailed',
  SignDocument = 'signDocument',
  SignDocumentSuccess = 'signDocumentSuccess',
  SignDocumentFailed = 'signDocumentFailed',
  ApproveDocument = 'approveDocument',
  ApproveDocumentSuccess = 'approveDocumentSuccess',
  ApproveDocumentFailed = 'approveDocumentFailed',
  LogAfterSignStep = 'logAfterSignStep',
  CreateSignup = 'createSignup',
  CreateSignupSuccess = 'createSignupSuccess',
  CreateSignupFailed = 'createSignupFailed',
  SendFeedback = 'sendFeedback',
  SendFeedbackFailed = 'sendFeedbackFailed',
  SetAfterSignStep = 'setAfterSignStep',
  CheckSignedDocument = 'checkSignedDocument',
  AddInitials = 'addInitials',
  SetSelectedInitialFieldId = 'setSelectedInitialFieldId',
  SetSelectedEid = 'setSelectedEid',
  SetCriiptoStatus = 'setCriiptoStatus',
  CriiptoRedirect = 'criiptoRedirect',
  ResetState = 'resetState',
}

export enum ESigningMutation {
  SetTransactionData = 'setTransactionData',
  SetAttemptCount = 'setAttemptCount',
  SetError = 'setError',
  SetStep = 'setStep',
  ResetBankId = 'resetBankId',
  SetBankIdSigningStep = 'setBankIdSigningStep',
  SetBankIdLoading = 'setBankIdLoading',
  SetSigningType = 'setSigningType',
  SetSigningStep = 'setSigningStep',
  SetIsLoading = 'setIsLoading',
  SetVerifyingSignature = 'verifyingSignature',
  SetSignatureError = 'signatureError',
  SetVerifyingSign = 'verifyingSign',
  SetSignError = 'clickError',
  SetVerifyingQna = 'verifyingQna',
  SetQnaError = 'qnaError',
  SetVerifyingSms = 'verifyingSms',
  SetSmsError = 'smsError',
  SetAfterSign = 'setAfterSign',
  SetBankIdHintCode = 'setBankIdHintCode',
  SetSendPinRetryCount = 'setSendPinRetryCount',
  SetInitials = 'setInitials',
  SetSelectedInitialFieldId = 'setSelectedInitialFieldId',
  SetCriiptoStatus = 'setCriiptoStatus',
  SetSelectedEid = 'setSelectedEid',
  SetState = 'setState',
}

export const getDefaultState = (): ISigningState => ({
  bankId: {
    step: EBankIdStep.Start,
    autoStartToken: '',
    attemptCount: 0,
    error: '',
    orderRef: '',
    ssn: '',
    signingType: EBankIdSigningType.None,
    isLoading: false,
    hintCode: undefined,
  },
  selectedEid: undefined,
  criiptoStatus: ECriiptoStatus.Default,
  qna: {
    isVerifying: false,
    error: false,
  },
  sms: {
    isVerifying: false,
    error: false,
    sendPinRetryCount: 0,
  },
  signature: {
    isVerifying: false,
    error: false,
  },
  sign: {
    isVerifying: false,
    error: false,
  },
  step: {
    type: null,
    data: null,
  },
  afterSign: {
    step: EAfterSignStep.Init,
    error: '',
    redirectUrl: '',
    didYouMean: '',
    loading: false,
  },
  loading: false,
  initials: '',
  selectedInitialFieldId: '',
});
